import {
  cloneElement,
  ComponentType,
  ElementType,
  HTMLAttributes,
  isValidElement,
  ReactElement,
  ReactNode,
  startTransition,
  UIEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { styled } from '@mui/material';
import clsx from 'clsx';
import { IconContext } from 'phosphor-react';

import { useElementWidth, useMergedRef } from '@cast/utils';

import InputChip, { InputChipProps } from './Chip';
import { chipGetters } from './chipInputTheme';
import { WithRef } from '../../../types';
import { mergeSx } from '../../../utils';
import { MagnifyingGlass, XCircle } from '../../icons';
import AutoSizingInput from '../AutoSizingInput';
import { Input, InputProps } from '../input';
import { tokens } from '../../../theme/generated/tokens';

const sizeToIconSize = {
  small: '16px',
  medium: '20px',
  large: '20px',
};

const sizeToChipSize = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
} as const;

type CustomInputProps = HTMLAttributes<HTMLInputElement> & {
  title?: string;
  value?: string;
};

const CustomInput = ({
  ref,
  title,
  ...props
}: WithRef<HTMLInputElement, CustomInputProps>) => {
  const [inputRef, setInputRef] = useMergedRef(ref, false);
  return (
    <div className="DsChipInput-InputWrapper">
      {title && (
        <span
          className="DsChipInput-ChipTitle"
          onClick={() => inputRef?.current?.focus()}
        >
          {title}
        </span>
      )}
      <AutoSizingInput
        {...props}
        ref={setInputRef}
        styleFactory={(width) => ({
          width: `${width ? width + 16 : 16}px !important`,
        })}
      />
    </div>
  );
};
CustomInput.displayName = 'CustomInput';

export type ChipInputProps<
  T = ReactElement | InputChipProps | string,
  C extends ElementType = any
> = Omit<Omit<InputProps, 'ref'>, 'onEnter' | 'inputRef'> & {
  chips?: Array<T>;
  startIcon?: ReactNode;
  activeTitle?: string;
  onClear?: (e: UIEvent) => void;
  showClearButton?: boolean;
  separators?: string[];
  onSeparation?: (value: string | string[]) => void;
  ChipComponent?: ComponentType<InputChipProps<C>>;
};

const StyledInput = styled(Input, {
  name: 'DsChipInput',
  slot: 'Root',
  overridesResolver: ({ size }, styles) => {
    return [styles.root, styles[size]];
  },
})({});
StyledInput.displayName = 'StyledInput';

export type ChipInputForwardRef = {
  wrapperRef?: HTMLDivElement | null;
  inputRef?: HTMLInputElement | null;
  clearInput: () => void;
};

const ChipInput = ({
  ref,
  chips,
  startIcon = true,
  activeTitle,
  inputProps = {},
  size = 'medium',
  onClear,
  showClearButton,
  separators = ['Enter'],
  onSeparation,
  ChipComponent = InputChip,
  placeholder,
  onBlur,
  ...props
}: WithRef<ChipInputForwardRef, ChipInputProps>) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState<string>('');

  useImperativeHandle(ref, () => ({
    wrapperRef: wrapperRef.current,
    inputRef: inputRef?.current,
    clearInput: () => {
      setValue('');
    },
  }));

  const showStartIcon = !!startIcon;
  if (showStartIcon && typeof startIcon === 'boolean') {
    startIcon = <MagnifyingGlass className="Ds-MagnifyingGlass" />;
  }
  const [startIconWrapper, setStartIconWrapper] =
    useState<HTMLDivElement | null>(null);
  const startIconWidth = useElementWidth(startIconWrapper);

  const [endIconWrapper, setEndIconWrapper] = useState<HTMLDivElement | null>(
    null
  );
  const endIconWidth = useElementWidth(endIconWrapper);

  const valueToChips = useCallback(
    (value: string) => {
      const textSeparators = separators.filter((s) => s.length === 1);
      const values =
        textSeparators.length > 0
          ? value.split(new RegExp(textSeparators.join('|'), 'g'))
          : [value];
      onSeparation?.(values.map((v) => v.trim()));
      setValue('');
    },
    [onSeparation, separators, setValue]
  );

  useEffect(() => {
    const textSeparators = separators?.filter((s) => s.length === 1);
    if (textSeparators?.some((s) => value.includes(s))) {
      startTransition(() => {
        valueToChips(value);
      });
    }
  }, [separators, value, valueToChips]);

  const triggerClear = useCallback(
    (event: UIEvent) => {
      onClear?.(event);
      setValue('');
      inputRef?.current?.focus();
    },
    [inputRef, onClear]
  );

  return (
    <StyledInput
      ref={wrapperRef}
      inputRef={inputRef}
      className={clsx(
        'DsChipInput-root',
        !!startIconWidth && 'DsChipInput-withStartIcon'
      )}
      InputProps={{
        inputComponent: CustomInput,
      }}
      inputProps={{
        ...inputProps,
        title: activeTitle,
        disabled: props.disabled,
      }}
      size={size}
      placeholder={chips?.length ? undefined : placeholder}
      {...props}
      sx={mergeSx(
        !!startIconWidth && {
          '& .MuiInputBase-root': {
            paddingLeft: `calc(${startIconWidth}px + ${chipGetters.px()} + ${chipGetters.startIconPr()})`,
          },
        },
        !!endIconWidth && {
          '& .MuiInputBase-root': {
            paddingRight: `calc(${endIconWidth}px + ${chipGetters.px()} + ${chipGetters.startIconPr()})`,
          },
        },
        props.sx
      )}
      value={value}
      onChange={(_, value) => setValue(value)}
      onKeyDownCapture={(e) => {
        const target = e.target as HTMLInputElement;
        if (separators?.includes(e.key)) {
          // The separator key is consumed by the chip input — it must not
          // also trigger the browser's implicit form submission (Enter
          // inside a <form> submits it).
          e.preventDefault();
          valueToChips(target.value);
        }
      }}
      onBlur={(e) => {
        valueToChips((e.target as HTMLInputElement).value);
        onBlur?.(e);
      }}
      startAdornment={
        <>
          {showStartIcon && (
            <div className="DsChipInput-StartIcon" ref={setStartIconWrapper}>
              <IconContext.Provider value={{ size: sizeToIconSize[size] }}>
                {startIcon}
              </IconContext.Provider>
            </div>
          )}
          {chips?.map((chip, index) => {
            if (isValidElement(chip)) {
              return cloneElement(chip, {
                className: 'DsChipInput-Chip',
                size: sizeToChipSize[size],
              } as InputChipProps);
            }
            const { sx, className, key, ...chipProps } =
              typeof chip === 'string' ? { title: chip } : chip;

            return (
              <ChipComponent
                key={key || index}
                className={clsx('DsChipInput-Chip', className)}
                sx={mergeSx(
                  {
                    backgroundColor: tokens.theme.bg.categorical.indigoStrong,
                    color: tokens.theme.fg.categorical.onIndigoStrong,
                  },
                  sx
                )}
                size={sizeToChipSize[size]}
                disabled={props.disabled || props.readOnly}
                multiline
                {...chipProps}
                onDestroy={
                  !props.disabled && !props.readOnly
                    ? chipProps.onDestroy
                    : undefined
                }
              />
            );
          })}
        </>
      }
      endAdornment={
        (showClearButton !== undefined
          ? showClearButton
          : onClear &&
            !!(activeTitle?.length || chips?.length || !!props.value)) &&
        !props.disabled &&
        !props.readOnly && (
          <div
            ref={setEndIconWrapper}
            className="DsChipInput-EndIcon"
            data-testid="clear-all"
          >
            <XCircle
              weight="fill"
              aria-label="clear all"
              size={sizeToIconSize[size]}
              onClick={triggerClear}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )
      }
    />
  );
};
ChipInput.displayName = 'ChipInput';

export default ChipInput;
